/* global $ */
import * as R from "ramda";
import { collectWithin } from "$json/lib/collect-values";
import { nameToPath, pathStringToArray } from "./string-helpers";

const on = (event, fun) => {
  $(document).on(event, fun);
};

const onWhen = (event, condition, fun) => {
  $(document).on(event, (...args) => {
    if (condition(...args)) fun(...args);
  });
};

const once = (event, fun) => {
  $(document).one(event, fun);
};

const onceWhen = (event, condition, fun) => {
  $(document).one(event, (...args) => {
    if (condition(...args)) fun(...args);
    else onceWhen(event, condition, fun);
  });
};

const custom = R.curry(
  (name, detail) =>
    new CustomEvent(name, {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail
    })
);

const command = custom("bb:command");

const dispatchEvent = R.invoker(1, "dispatchEvent");

const dispatchCommand = R.curry((type, payload, event) => {
  if (event.target.isConnected) {
    return dispatchEvent(command({ type, payload }), event.target);
  }
  throw new Error(
    `Event.target is not connected. Tried to dispatch command <${type}> with payload ${JSON.stringify(
      payload
    )}, but target is not connected anymore.`
  );
});

const JSONBuilder = () => {
  let ob = {};
  let p = {};
  return {
    append(key, value) {
      ob = R.assocPath(pathStringToArray(key), value, ob);
      p[key] = value;
    },
    get object() {
      return ob;
    },
    get paths() {
      return p;
    }
  };
};

export const checkForm = paths => (object, target) => {
  for (let [path, check] of Object.entries(paths)) {
    if (!check(nameToPath(path)(object, target))) {
      return false;
    }
  }
  return true;
};

const INPUT_SELECTOR = R.compose(
  R.join(", "),
  R.map(c => c + "[name]:not([disabled])")
)(["input", "text-area", "select"]);

const inputsOf = container => container.querySelectorAll(INPUT_SELECTOR);

export const collectFromEvent = R.curry(({ container }, event) =>
  R.compose(R.prop("object"))(
    collectWithin({
      params: JSONBuilder(), // Object with an .append method, taking two params
      container: container || event.currentTarget,
      omitFn: () => false, // Keep checkboxes
      changeFn: R.identity, // Do not change true to "on"
      collector: inputsOf
    })
  )
);

const onMatchDo = R.curry((matchFun, fun, event) => {
  let element = event.target;
  while (element) {
    if (matchFun(element)) {
      fun(element, event);
      break;
    }
    element = element.parentNode;
    if (element === null) break;
    if (!element.tagName) break; // A way of excluding document and shadowRoots.
  }
});

const onLocalCommand = onMatchDo(e => e.hasAttribute("data-cmd"));

const preventDefault = R.tap(R.invoker(0, "preventDefault"));
const stopPropagation = R.tap(R.invoker(0, "stopPropagation"));
const stopImmediatePropagation = R.tap(
  R.invoker(0, "stopImmediatePropagation")
);
export const stopAllPropagation = R.tap(
  R.juxt([stopImmediatePropagation, stopPropagation])
);

const originalTargetPropName = "originalTarget";
const composedPathMethodName = "composedPath";
const composedPath = R.invoker(0, composedPathMethodName);

const currentTarget = R.prop("currentTarget");
const target = R.prop("target");

const originalTarget = R.cond([
  [R.hasIn(originalTargetPropName), R.prop(originalTargetPropName)],
  [R.hasIn(composedPathMethodName), R.compose(R.head, composedPath)],
  [R.T, R.always(null)]
]);

const pastedData = R.compose(
  data => data.getData("text/plain"),
  R.either(R.prop("clipboardData"), R.prop("dataTransfer"))
);

const isEventOnExternalLink = R.propSatisfies(
  R.invoker(1, "matches")(`a[href]:not([href^="#"])`),
  "target"
);

const openElsewhere = R.juxt([
  R.compose(window.open, R.path(["target", "href"])),
  preventDefault
]);

const virtualFromPointerEvent = ({ clientX, clientY }) => ({
  getBoundingClientRect() {
    return {
      width: 0,
      height: 0,
      x: clientX,
      y: clientY,
      top: clientY,
      left: clientX,
      right: clientX,
      bottom: clientY
    };
  }
});

const maybeVirtualFromPointerEvent = R.ifElse(
  R.propSatisfies(button => button > 1, "button"),
  virtualFromPointerEvent,
  R.always(undefined)
);

export {
  on,
  onWhen,
  once,
  onceWhen,
  custom,
  command,
  dispatchCommand,
  onLocalCommand,
  pastedData,
  preventDefault,
  stopPropagation,
  currentTarget,
  target,
  originalTarget,
  dispatchEvent,
  isEventOnExternalLink,
  openElsewhere,
  composedPath,
  virtualFromPointerEvent,
  maybeVirtualFromPointerEvent
};
